
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

/**
 * Exports data to CSV format and triggers download
 * @param data Array of data to export 
 * @param filename The name of the file (without extension)
 */
export const exportToCSV = <T extends Record<string, any>>(data: T[], filename: string) => {
  try {
    // Create CSV content
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(","),
      ...data.map((item) =>
        headers.map((key) => {
          const value = item[key];
          // Handle values that might contain commas or quotes
          if (value === null || value === undefined) return '""';
          const strValue = String(value);
          if (strValue.includes(",") || strValue.includes('"') || strValue.includes('\n')) {
            return `"${strValue.replace(/"/g, '""')}"`;
          }
          return strValue;
        }).join(",")
      ),
    ].join("\n");
    
    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `${filename}.csv`);
  } catch (error) {
    console.error("Error exporting to CSV:", error);
  }
};

/**
 * Exports data to a styled Excel file and triggers download
 */
export const exportToExcel = async <T extends Record<string, any>>(data: T[], filename: string) => {
  try {
    if (!data || data.length === 0) return;
    const ExcelJS = await import("exceljs");
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "EDLR";
    workbook.created = new Date();
    const sheet = workbook.addWorksheet("Données", {
      views: [{ state: "frozen", ySplit: 1 }],
    });

    const headers = Object.keys(data[0]);
    sheet.columns = headers.map((h) => ({ header: h, key: h }));

    data.forEach((row) => {
      sheet.addRow(headers.reduce((acc, h) => {
        const v = row[h];
        acc[h] = v === null || v === undefined ? "" : v;
        return acc;
      }, {} as Record<string, any>));
    });

    // Header styling (bleu royal EDLR, texte blanc)
    const headerRow = sheet.getRow(1);
    headerRow.height = 24;
    headerRow.eachCell((cell) => {
      cell.font = { name: "Arial", bold: true, size: 11, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E3A8A" } };
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
      cell.border = {
        top: { style: "thin", color: { argb: "FF1E3A8A" } },
        left: { style: "thin", color: { argb: "FF1E3A8A" } },
        bottom: { style: "thin", color: { argb: "FF1E3A8A" } },
        right: { style: "thin", color: { argb: "FF1E3A8A" } },
      };
    });

    // Body styling: bandes alternées + bordures légères
    for (let r = 2; r <= sheet.rowCount; r++) {
      const row = sheet.getRow(r);
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.font = { name: "Arial", size: 10, color: { argb: "FF1F2937" } };
        cell.alignment = { vertical: "middle", wrapText: false };
        if (r % 2 === 0) {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F4F6" } };
        }
        cell.border = {
          bottom: { style: "hair", color: { argb: "FFD1D5DB" } },
          right: { style: "hair", color: { argb: "FFD1D5DB" } },
        };
      });
    }

    // Largeur des colonnes ajustée au contenu
    sheet.columns.forEach((col) => {
      let max = String(col.header ?? "").length;
      col.eachCell?.({ includeEmpty: false }, (cell) => {
        const len = String(cell.value ?? "").length;
        if (len > max) max = len;
      });
      col.width = Math.min(Math.max(max + 3, 10), 45);
    });

    // Filtres automatiques sur l'en-tête
    sheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: headers.length },
    };

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `${filename}.xlsx`
    );
  } catch (error) {
    console.error("Error exporting to Excel:", error);
  }
};


/**
 * Format members data for export by transforming values to be more readable
 */
export const formatMembersForExport = (members: any[]) => {
  return members.map(member => ({
    Prénom: member.first_name,
    Nom: member.last_name,
    Email: member.email,
    "Montant": member.amount,
    "Don additionnel": member.additional_donation || 0,
    "Type d'adhésion": member.membership_type,
    "Statut du paiement": member.payment_status === "paid" ? "Payé" : "Non payé",
    "Date d'adhésion": new Date(member.created_at).toLocaleDateString('fr-FR')
  }));
};
